import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LaundryContext } from '../Components/Contexts/LaundryContext';
import {
    FormControl,
    FormLabel,
    Input,
    Button,
    VStack,
    Image,
    useToast,
    Box,
    Heading,
    Text,
    HStack,
} from '@chakra-ui/react';
import PlacesSuggestInput from '../Components/SiteLanding/PlacesSuggestInput';
import { bookingDestination, isCustomerBookingToken } from '../utils/bookingRedirect';
import axios from "axios";
import LaundryPickupImage from "../images/laundry-pickup.svg";

// Format a raw phone string into (AAA) BBB-CCCC for display. Strips a leading
// country code (1) and any non-digits. Falls back to the original if it can't
// find 10 digits.
const formatPhone = (raw) => {
    if (!raw) return '';
    let digits = String(raw).replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
    if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return raw;
};

// Build a tel: href — keep digits with a leading + for the country code so it
// dials correctly on mobile.
const telHref = (raw) => {
    if (!raw) return '';
    let digits = String(raw).replace(/\D/g, '');
    if (digits.length === 10) digits = `1${digits}`;
    return `tel:+${digits}`;
};

const Address = () => {
    const { laundryData } = useContext(LaundryContext);
    const { laundryId } = useParams();
    const navigate = useNavigate();
    // If user already has a valid session (token + address), skip to order flow
    useEffect(() => {
        const token = localStorage.getItem('idToken');
        const savedAddress = localStorage.getItem('customerAddress');
        // A counter sign-in uses the same idToken key. It must not skip the address form.
        if (isCustomerBookingToken(token) && savedAddress) {
            const pending = localStorage.getItem('pendingRedirect');
            if (pending) localStorage.removeItem('pendingRedirect');
            navigate(bookingDestination(pending, laundryId), { replace: true });
        }
    }, [laundryId, navigate]);

    const [address, setAddress] = useState('');
    const [isAddressSelected, setIsAddressSelected] = useState(false);
    const toast = useToast();
    const [validatingAddress, setValidatingAddress] = useState(false);
    const [showNotifyForm, setShowNotifyForm] = useState(false);
    const [notifyEmail, setNotifyEmail] = useState('');
    const [notifyPhone, setNotifyPhone] = useState('');
    const [notifySubmitting, setNotifySubmitting] = useState(false);
    // When an address is serviceable by zip but beyond the shop's max delivery
    // distance, the backend returns reason:"too_far" + the shop's phone.
    const [tooFar, setTooFar] = useState(null); // { contactPhone, distanceMi, maxDistanceMi } | null

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!address.trim()) {
            toast({ title: "Address Required", description: "Please type your address and pick from the suggestions.", status: "error", duration: 3000, isClosable: true });
            return;
        }
        if (!isAddressSelected) {
            toast({ title: "Invalid Address", description: "Please select a valid address from the suggestions.", status: "error", duration: 3000, isClosable: true });
            return;
        }
        try {
            setValidatingAddress(true);
            const response = await axios.get(
                `${process.env.REACT_APP_AWS_API_URL}/api/laundry/validate-address`,
                { params: { operation: 'validateAddress', laundryId, address }, headers: { 'x-api-key': process.env.REACT_APP_AWS_API_KEY } }
            );
            const data = response.data;
            if (data.status === 'success') {
                if (data.serviceable) {
                    setTooFar(null);
                    localStorage.setItem('customerAddress', address);
                    navigate(`/${laundryId}/login`);
                } else if (data.reason === 'too_far') {
                    // Zip is serviced, but the address is beyond the max delivery
                    // distance. The backend already recorded it as demand; show a
                    // "please call us" message with the shop's number.
                    // The inline "outside delivery range" panel (rendered from
                    // tooFar) already shows a nicely formatted, tappable phone
                    // number and a dismiss action, so we don't also fire a toast
                    // (which duplicated the message with an unformatted number).
                    setTooFar({
                        contactPhone: data.contactPhone || '',
                        distanceMi: data.distanceMi,
                        maxDistanceMi: data.maxDistanceMi,
                    });
                    setShowNotifyForm(false);
                } else {
                    setTooFar(null);
                    setShowNotifyForm(true);
                    toast({ title: "Not Serviceable", description: 'This area is not yet serviced. Leave your info to be notified when we expand!', status: "info", duration: 5000, isClosable: true });
                }
            } else {
                toast({ title: "Error", description: `Error: ${data.message}`, status: "error", duration: 3000, isClosable: true });
            }
        } catch (error) {
            toast({ title: "Error", description: 'An error occurred. Please try again.', status: "error", duration: 3000, isClosable: true });
        } finally {
            setValidatingAddress(false);
        }
    };

    const handlePlaceSelected = ({ formattedAddress }) => {
        setAddress(formattedAddress);
        setIsAddressSelected(true);
    };

    const handleAddressChange = (text) => {
        setAddress(text);
        setIsAddressSelected(false);
        // Editing the address dismisses the outside-range warning.
        if (tooFar) setTooFar(null);
    };

    const themeGradient = (() => {
        const gradientMap = {
            blue: "linear-gradient(180deg, #EBF8FF 0%, #BEE3F8 100%)",
            green: "linear-gradient(180deg, #F0FFF4 0%, #C6F6D5 100%)",
            purple: "linear-gradient(180deg, #FAF5FF 0%, #D6BCFA 100%)",
            teal: "linear-gradient(180deg, #E6FFFA 0%, #81E6D9 100%)",
            orange: "linear-gradient(180deg, #FFFAF0 0%, #FBD38D 100%)",
            red: "linear-gradient(180deg, #FFF5F5 0%, #FEB2B2 100%)",
            pink: "linear-gradient(180deg, #FFF5F7 0%, #FBB6CE 100%)",
            cyan: "linear-gradient(180deg, #EDFDFD 0%, #9DECF9 100%)",
        };
        return gradientMap[laundryData?.themeColor] || gradientMap.blue;
    })();

    return (
        <Box bg={themeGradient} minH="100vh" display="flex" flexDirection="column" alignItems="center" justifyContent="center" px={[5, 7, 9]} py={[6, 8]}>
            <Box w="full" maxW="500px">
                <VStack spacing={[2, 3]} align="center">
                    {/* Logo — prominent branding */}
                    {laundryData?.laundryLogo && (
                        <Image
                            src={laundryData.laundryLogo}
                            alt={laundryData?.laundryName}
                            objectFit="contain"
                            maxW={{ base: '220px', md: '280px' }}
                            maxH={{ base: '100px', md: '120px' }}
                        />
                    )}
                    <Image src={LaundryPickupImage} alt="Pickup & Delivery" objectFit="contain" w={{ base: '140px', md: '160px' }} h={{ base: '90px', md: '100px' }} />
                    <Heading size={['sm', 'md']} color="blue.700" textAlign="center" fontWeight="700">
                        Welcome to {laundryData?.laundryName}
                    </Heading>
                    <Text fontSize={['xs', 'sm']} color="gray.600" textAlign="center" maxW="360px">
                        Pickup & delivery at your doorstep. Fresh clothes, zero hassle.
                    </Text>
                </VStack>

                <VStack as="form" onSubmit={handleSubmit} spacing={[3, 4]} mt={[3, 4]} w="full" align="stretch">
                    <FormControl id="address" isRequired>
                        <FormLabel fontSize={["sm", "md", "lg"]} color="blue.600" mb={2}>
                            Enter your address to schedule a pickup
                        </FormLabel>
                        <PlacesSuggestInput
                            placeholder="Type and select your Address"
                            value={address}
                            onChange={handleAddressChange}
                            onSelect={handlePlaceSelected}
                            size="lg"
                            boxShadow="sm"
                            focusBorderColor="blue.500"
                            borderColor="blue.300"
                            fontSize={["sm", "md"]}
                        />
                    </FormControl>

                    <Button type="submit" colorScheme="blue" size="lg" alignSelf="center" isLoading={validatingAddress} loadingText="Validating Address" boxShadow="md" w={["full","auto"]} px={10} borderRadius="full" fontWeight="600">
                        Schedule Pickup
                    </Button>

                    {/* Trust badges */}
                    <HStack spacing={[3, 5]} justify="center" pt={1} flexWrap="wrap">
                        <VStack spacing={0}>
                            <Text fontSize="lg">🚚</Text>
                            <Text fontSize="xs" color="gray.500" fontWeight="500">Pickup</Text>
                        </VStack>
                        <VStack spacing={0}>
                            <Text fontSize="lg">⚡</Text>
                            <Text fontSize="xs" color="gray.500" fontWeight="500">Same Day</Text>
                        </VStack>
                        <VStack spacing={0}>
                            <Text fontSize="lg">💳</Text>
                            <Text fontSize="xs" color="gray.500" fontWeight="500">Pay Later</Text>
                        </VStack>
                        <VStack spacing={0}>
                            <Text fontSize="lg">📍</Text>
                            <Text fontSize="xs" color="gray.500" fontWeight="500">Live Tracking</Text>
                        </VStack>
                    </HStack>

                    {/* Outside delivery range — serviceable zip but beyond max distance */}
                    {tooFar && (
                        <Box bg="orange.50" borderRadius="xl" p={5} boxShadow="md" w="full" mt={4} border="1px solid" borderColor="orange.200">
                            <VStack spacing={2} align="stretch">
                                <HStack justify="space-between" align="start">
                                    <Text fontWeight="700" color="orange.700" fontSize="sm">
                                        This address is outside our delivery range
                                    </Text>
                                    <Button
                                        variant="ghost"
                                        size="xs"
                                        color="orange.700"
                                        onClick={() => setTooFar(null)}
                                        aria-label="Dismiss"
                                    >
                                        ✕
                                    </Button>
                                </HStack>
                                <Text fontSize="xs" color="gray.600">
                                    We'd love to serve you! This address is a bit too far for our standard delivery.
                                    {tooFar.contactPhone ? ' Please give us a call and we\'ll see what we can do.' : ' Please contact us and we\'ll see what we can do.'}
                                </Text>
                                {tooFar.contactPhone && (
                                    <Button
                                        as="a"
                                        href={telHref(tooFar.contactPhone)}
                                        size="sm"
                                        colorScheme="orange"
                                    >
                                        Call {formatPhone(tooFar.contactPhone)}
                                    </Button>
                                )}
                            </VStack>
                        </Box>
                    )}

                    {/* Notify Me Form — shows when address is not serviceable */}
                    {showNotifyForm && (
                        <Box bg="white" borderRadius="xl" p={5} boxShadow="md" w="full" mt={4}>
                            <VStack spacing={3} align="stretch">
                                <Text fontWeight="600" color="gray.700" fontSize="sm">
                                    We don't serve this area yet, but we're expanding!
                                </Text>
                                <Text fontSize="xs" color="gray.500">
                                    Leave your email or phone and we'll notify you when we're available in your area.
                                </Text>
                                <Input
                                    placeholder="Email address"
                                    value={notifyEmail}
                                    onChange={(e) => setNotifyEmail(e.target.value)}
                                    size="sm"
                                    type="email"
                                />
                                <Input
                                    placeholder="Phone number (optional)"
                                    value={notifyPhone}
                                    onChange={(e) => setNotifyPhone(e.target.value)}
                                    size="sm"
                                />
                                <Button
                                    size="sm"
                                    colorScheme="green"
                                    isLoading={notifySubmitting}
                                    onClick={async () => {
                                        if (!notifyEmail && !notifyPhone) {
                                            toast({ title: "Please provide email or phone", status: "warning", duration: 3000 });
                                            return;
                                        }
                                        setNotifySubmitting(true);
                                        try {
                                            await axios.post(`${process.env.REACT_APP_AWS_API_URL}/api/laundry/zip-interest`, {
                                                laundryId, address, email: notifyEmail, phone: notifyPhone,
                                                zipCode: address.match(/\d{5}/)?.[0] || '',
                                            });
                                            toast({ title: "Got it!", description: "We'll reach out when we expand to your area.", status: "success", duration: 5000 });
                                            setShowNotifyForm(false);
                                        } catch (err) {
                                            toast({ title: "Error", description: "Please try again.", status: "error", duration: 3000 });
                                        } finally {
                                            setNotifySubmitting(false);
                                        }
                                    }}
                                >
                                    Notify Me
                                </Button>
                            </VStack>
                        </Box>
                    )}
                </VStack>
            </Box>
        </Box>
    );
};

export default Address;
